import axios from 'axios';
import moment from 'moment';

import { urls } from '~/preloaded';
import settings from '~/settings';
import { BATTLE_TYPES, TREASURY_TABS } from '~/constants';
import { get, post, put } from '~/helpers/api';
import { isClanPreloaded } from '~/helpers/clan';
import { hideDistributionResultDialog, showDistributionDialog } from '~/helpers/dialogs';
import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';
import { sendErrorNotification } from '~/web2ClientAPI/base';
import { distributeResourcesError } from '~/web2ClientAPI/notifications';
import { actionsClanProfile, fetchClan, fetchMembers } from '~/Actions/ActionClanProfile';
import { actionsProcessing } from '~/Actions/ActionProcessing';
import { actionsTreasury, TRANSACTIONS_STATUSES } from '~/Actions/ActionTreasury';

import type { CurrencyType } from '@wg/wows-react-uikit/components/Currency/Currency';
import type { Moment } from 'moment';
import type { RegularRewardsOffersCooldown } from '~/Reducers/ReducerTreasury';
import type { AppThunk, AppAsyncThunk } from '~/store';
import type { Balance, DayTransactions, Transaction, TransactionUpdatingStatus } from '~/types/declaration';

const getTotalTransactionsSumThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const { filterStartDate, filterEndDate, selectedTab } = state.ReducerTreasury;
  if (urls.clanTreasuryOperationsTotals) {
    let url = urls.clanTreasuryOperationsTotals;

    const connector = url.indexOf('?') !== -1 ? '&' : '?';

    url = `${url}${connector}type=${getTransactiontype(selectedTab)}`;

    if (filterStartDate && filterEndDate) {
      const start = filterStartDate.clone().utc().format();
      const end = filterEndDate.clone().utc().format();
      url = `${url}&since=${start}&until=${end}`;
    }

    void axios.get<Balance>(url).then((response) => {
      if (response.status === 200 && response.data && response.data.length) {
        dispatch(actionsTreasury.getTotalTransactionsSum(response.data));
      }
    });
  }
};

export const getTotalBalanceThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const clanId = state.currentAccount.clanId;

  const clan = state.clans.items[String(clanId)];
  const personalResource = clan && !isClanPreloaded(clan) && clan.personalResource ? clan.personalResource : 0;

  const personalResourceBalanceOItem = {
    type: 'oil' as CurrencyType,
    amount: personalResource,
    canDistribute: false,
  };

  if (urls.clanTreasuryBalance) {
    const url = urls.clanTreasuryBalance;
    axios
      .get<Balance>(url)
      .then((response) => {
        const totalBalanceList: Balance = response.data.map((item) => {
          return {
            type: item.type,
            amount: item.amount,
            canDistribute: item.type !== 'oil',
          };
        });
        totalBalanceList.push(personalResourceBalanceOItem);

        dispatch(actionsTreasury.getTotalBalance(totalBalanceList));
      })
      .catch(() => {
        dispatch(actionsTreasury.getTotalBalance([]));
      });
  } else {
    console.error('no clanTreasuryBalance url');
  }
};

export const setFilterDatesThunk =
  (start: Moment | null, end: Moment | null): AppThunk =>
  (dispatch) => {
    if (start) {
      start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }

    if (end) {
      end.set({ hour: 23, minute: 59, second: 59, millisecond: 0 });
    }

    dispatch(actionsTreasury.setFilterDates(start, end));
    dispatch(filterTransactionsThunk());
  };

const filterTransactionsThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const startDate = state.ReducerTreasury.filterStartDate;
  const endDate = state.ReducerTreasury.filterEndDate;

  if ((startDate && endDate) || (!startDate && !endDate)) {
    dispatch(getTransactionsThunk(false));
  }
};

export const init = (): AppAsyncThunk => async (dispatch, getState) => {
  const state = getState();
  const clanId = state.currentAccount.clanId;

  if (!clanId) {
    return;
  }

  dispatch(actionsClanProfile.changeClanBattleType(clanId, BATTLE_TYPES.REGULAR_CVC));
  void dispatch(fetchMembers(clanId, false, 'cvc_or_brawl'));
  await dispatch(fetchClan(clanId, false));
  dispatch(getTreasuryConfigThunk());
  dispatch(getTransactionsThunk(false));
};

const sortByDateString = (left: { date: string }, right: { date: string }) => {
  return -moment.utc(left.date).diff(moment.utc(right.date));
};

const getTransactiontype = (index: TREASURY_TABS): string => {
  switch (index) {
    case TREASURY_TABS.INCOME: {
      return 'debit';
    }
    default: {
      return 'credit';
    }
  }
};

export const getTransactionsThunk =
  (isFromAppend: boolean): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const { selectedTab, filterStartDate, filterEndDate, isAppending, transactionsOffset, lastOffet } =
      state.ReducerTreasury;
    const clanId = state.currentAccount.clanId;

    if (!clanId) {
      return;
    }

    if (lastOffet === transactionsOffset && isAppending) {
      return;
    }

    if (!isFromAppend) {
      dispatch(getTotalBalanceThunk());
    }

    if (urls.clanTreasuryOperations) {
      if (!isAppending) {
        dispatch(actionsTreasury.setIsTransactionsUpdating(true));
      }

      let url = urls.clanTreasuryOperations;

      const connector = url.indexOf('?') !== -1 ? '&' : '?';

      url = `${url}${connector}type=${getTransactiontype(selectedTab)}`;

      if (filterStartDate && filterEndDate) {
        const start = filterStartDate.clone().utc().format();
        const end = filterEndDate.clone().utc().format();
        url = `${url}&since=${start}&until=${end}`;
      }

      if (isAppending) {
        url = `${url}&offset=${transactionsOffset}`;
      }

      axios
        .get<Transaction[]>(url)
        .then((response) => {
          const data = response.data;

          const transactionsListRaw: Record<string, Transaction[]> = {};

          if (data && data.length) {
            const innerTransactions: Record<number, Transaction[]> = {};

            data.forEach((transaction) => {
              if (transaction.data.parentId) {
                if (innerTransactions[transaction.data.parentId]) {
                  innerTransactions[transaction.data.parentId].push(transaction);
                } else {
                  innerTransactions[transaction.data.parentId] = [transaction];
                }
              }
            });

            data.forEach((item) => {
              const dateKey = item && item.date ? moment(item.date).format('DD_MM_YYYY') : null;
              if (dateKey) {
                if (!item.data.parentId) {
                  const mergedItem = {
                    ...item,
                  };
                  if (innerTransactions[item.id]) {
                    mergedItem.childTransactions = innerTransactions[item.id];
                  }
                  if (!transactionsListRaw[dateKey]) {
                    transactionsListRaw[dateKey] = [mergedItem];
                  } else {
                    transactionsListRaw[dateKey].push(mergedItem);
                  }
                }
              }
            });
          }

          const transactionRaw: DayTransactions[] = [];

          for (const key in transactionsListRaw) {
            if (Object.prototype.hasOwnProperty.call(transactionsListRaw, key)) {
              const oneDayTransactions = transactionsListRaw[key];
              const oneDaySorted = oneDayTransactions.sort(sortByDateString);
              transactionRaw.push({
                date: moment.utc(key, 'DD_MM_YYYY').toISOString(),
                transactions: oneDaySorted,
              });
            }
          }

          const transactionsSorted = transactionRaw.sort(sortByDateString);

          dispatch(actionsTreasury.getTransactions(transactionsSorted, transactionsOffset));
          dispatch(actionsTreasury.setIsTransactionsUpdating(false));
          dispatch(getTotalTransactionsSumThunk());
          dispatch(listenTransactionsStatusUpdatesThunk());
        })
        .catch((err) => {
          console.error(err);
          dispatch(actionsTreasury.setIsTransactionsUpdating(false));
        });
    } else {
      console.error('no url to get transactions');
    }
  };

export const distributeResources =
  (onSuccess: () => void): AppThunk =>
  (dispatch, getState) => {
    dispatch(actionsProcessing.startFetching());
    const state = getState();
    const clanId = state.currentAccount.clanId;
    const url = urls.clanTreasury.replace('{clan_id}', String(clanId));

    axios
      .put(url, {
        currency: state.ReducerTreasury.selectedDistributionCurrency,
        delta: state.ReducerTreasury.distributionSum,
        initiator_id: state.currentAccount.id,
        receivers: state.ReducerTreasury.distributionList,
      })
      .then(() => {
        dispatch(actionsProcessing.stopFetching());
        // TODO: refactor clear state
        dispatch(actionsTreasury.setSelectedTab(TREASURY_TABS.EXPENSES));
        onSuccess?.();
      })
      .catch(() => {
        dispatch(actionsProcessing.stopFetching());
        distributeResourcesError();
      });
  };

const listenTransactionsStatusUpdatesThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const transactions = state.ReducerTreasury.transactions;

  const listeningIds: number[] = [];

  transactions.forEach((dayTransactions) => {
    if (dayTransactions && dayTransactions.transactions) {
      dayTransactions.transactions.forEach((singleTransaction) => {
        if (singleTransaction?.status === TRANSACTIONS_STATUSES.PENDING) {
          if (singleTransaction.data?.gameTransactionId) {
            listeningIds.push(singleTransaction.data.gameTransactionId);
          }
        }
        if (singleTransaction.childTransactions?.length) {
          singleTransaction.childTransactions.forEach((childTransaction) => {
            if (childTransaction?.status === TRANSACTIONS_STATUSES.PENDING) {
              if (childTransaction.data?.gameTransactionId) {
                listeningIds.push(childTransaction.data.gameTransactionId);
              }
            }
          });
        }
      });
    }
  });

  if (listeningIds.length > 0) {
    const url = `${urls.clanTransactions}${
      urls.clanTransactions.indexOf('?') !== -1 ? '&' : '?'
    }ids=${listeningIds.join(',')}`;

    axios
      .get<TransactionUpdatingStatus[]>(url)
      .then((response) => {
        if (response.status === 200) {
          dispatch(actionsTreasury.listenTransactionsStatusUpdates(response.data));

          setTimeout(() => {
            dispatch(listenTransactionsStatusUpdatesThunk());
          }, 1000);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  } else {
    void dispatch(updateTreasuryBoxesCoolDownThunk());
    void dispatch(syncAccountInfoThunk());
  }
};

export const appendTransactionsThunk = (): AppThunk => (dispatch) => {
  dispatch(actionsTreasury.appendTransactions());
  dispatch(getTransactionsThunk(true));
};

export const repeatFailedDistributionThunk = (): AppThunk => (dispatch, getState) => {
  const state = getState();
  const transaction = { ...state.ReducerTreasury.detailViewTransaction };
  const firstReceiver = transaction?.data?.receivers?.[0] || null;
  if (!firstReceiver) {
    return;
  }

  const currencyType = firstReceiver.delta.type;
  const distributionSumm = firstReceiver.delta.amount;
  const members = transaction.data?.failedReceivers || [];

  dispatch(hideDistributionResultDialog());
  dispatch(actionsTreasury.selectDistributionCurrency(currencyType));
  dispatch(actionsTreasury.setDistributionSum(distributionSumm));
  dispatch(actionsTreasury.toggleDistributionMembers(members, false));
  dispatch(showDistributionDialog());
};

const getTreasuryConfigThunk = (): AppThunk => (dispatch) => {
  if (!settings.treasury) {
    return;
  }

  dispatch(actionsTreasury.getTreasuryConfig(settings.treasury));
  void dispatch(updateTreasuryBoxesCoolDownThunk());
};

export const buyTreasuryBoxThunk =
  (offerId: string): AppAsyncThunk =>
  async (dispatch) => {
    if (!urls.clanTreasuryBuyReward) {
      return Promise.resolve();
    }

    dispatch(actionsProcessing.startFetching());

    try {
      const url = urls.clanTreasuryBuyReward;
      const payload = { offer: offerId };
      await put(url, payload);

      dispatch(getTransactionsThunk(false));
      void dispatch(updateTreasuryBoxesCoolDownThunk());
      void dispatch(syncAccountInfoThunk());
    } catch (error: unknown) {
      sendErrorNotification();
    } finally {
      dispatch(actionsProcessing.stopFetching());
    }
  };

const updateTreasuryBoxesCoolDownThunk = (): AppAsyncThunk => async (dispatch) => {
  if (!urls.clanTreasuryRewards) {
    return Promise.resolve();
  }

  try {
    const url = urls.clanTreasuryRewards;
    const response = await get<RegularRewardsOffersCooldown>(url);

    dispatch(actionsTreasury.updateTreasuryBoxesCoolDown(response));
  } catch (error: unknown) {
    console.error(error);
  } finally {
    dispatch(actionsProcessing.stopFetching());
  }
};

export const repeatTreasuryBoxTransactionThunk =
  (id: number): AppAsyncThunk =>
  async (dispatch) => {
    if (!urls.clanTreasuryBuyRewardRepeat) {
      return Promise.resolve();
    }

    try {
      const url = urls.clanTreasuryBuyRewardRepeat;
      const payload = { treasury_transaction_id: id };
      await post(url, payload);

      dispatch(getTransactionsThunk(false));
    } catch (error: unknown) {
      console.error(error);
    }
  };
